import React, { useState } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaPercent, FaDollarSign, FaCheck, FaX, FaTag } from 'react-icons/fa6';
import { updateDiscountForReceipt } from '../../../../../../../../../../crud/payments_api/payment_receipts';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import './PostPaymentDiscount.scss';

interface PostPaymentDiscountProps {
  receiptId: number;
  totalPayed: number;
  currentDiscount: number;
  onDiscountApplied: (newDiscountAmount: number) => void;
  onClose: () => void;
}

const PostPaymentDiscount: React.FC<PostPaymentDiscountProps> = ({
  receiptId,
  totalPayed,
  currentDiscount,
  onDiscountApplied,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [discountType, setDiscountType] = useState<"percentage" | "amount">("percentage");
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [discountRawAmount, setDiscountRawAmount] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  const applyPostPaymentDiscount = () => {
    if (loading) return;
    
    // Validate comment is required
    if (!comment.trim()) {
      toast.error('Debes agregar un comentario explicando el motivo del descuento');
      return;
    }
    
    setLoading(true);

    let calculatedDiscount = 0;

    if (discountType === 'percentage') {
      if (discountPercentage < 0 || discountPercentage > 100) {
        toast.error('El porcentaje debe estar entre 0 y 100.');
        setLoading(false);
        return;
      }
      calculatedDiscount = (discountPercentage / 100) * totalPayed;
    } else {
      if (discountRawAmount < 0 || discountRawAmount > totalPayed) {
        toast.error('El monto debe estar entre 0 y el total pagado.');
        setLoading(false);
        return;
      }
      calculatedDiscount = discountRawAmount;
    }

    // Round to avoid decimal issues
    calculatedDiscount = Math.round(calculatedDiscount);

    updateDiscountForReceipt(receiptId, calculatedDiscount, comment.trim())
      .then(() => {
        toast.success('Descuento aplicado correctamente al recibo pagado');
        onDiscountApplied(currentDiscount + calculatedDiscount);
        setLoading(false);
        onClose();
      })
      .catch((error) => {
        console.error('Error applying post-payment discount: ', error);
        const errorMessage = error.response?.data?.message || 'Error aplicando descuento';
        toast.error(errorMessage);
        setLoading(false);
      });
  };

  return (
    <>
      <div className="post-payment-discount-panel">
        <div className="discount-header">
          <div className="discount-header-icon">
            <FaTag />
          </div>
          <div className="discount-header-text">
            <h4>Aplicar descuento post-pago</h4>
            <p>Este descuento se aplicará a un recibo ya pagado</p>
          </div>
        </div>

        <div className="current-discount-info">
          <div className="info-row">
            <span className="label">Total pagado:</span>
            <span className="value">{formatCurrency(totalPayed)}</span>
          </div>
          {currentDiscount > 0 && (
            <div className="info-row">
              <span className="label">Descuento actual:</span>
              <span className="value discount">{formatCurrency(currentDiscount)}</span>
            </div>
          )}
        </div>

        <div className="discount-type-toggle">
          <Button
            variant='none'
            className={`discount-type-button ${discountType === "percentage" ? "active" : ""}`}
            onClick={() => setDiscountType("percentage")}
            disabled={loading}
          >
            Porcentaje <FaPercent />
          </Button>
          <Button
            className={`discount-type-button ${discountType !== "percentage" ? "active" : ""}`}
            variant='none'
            onClick={() => setDiscountType("amount")}
            disabled={loading}
          >
            Monto <FaDollarSign />
          </Button>
        </div>

        <div className="discount-content">
          {/* Row 1: Labels */}
          <div className="labels-row">
            <div className="input-label">
              {discountType === "percentage" ? "Porcentaje de descuento" : "Monto a descontar"}
            </div>
            <div className="preview-label">Nuevo total</div>
          </div>

          {/* Row 2: Input and Preview */}
          <div className="content-row">
            <div className="discount-input-wrapper">
              {discountType === "percentage" ? (
                <InputGroup className="input-group">
                  <Form.Control
                    type="text"
                    value={discountPercentage === 0 ? "" : discountPercentage}
                    placeholder="0"
                    onChange={(e) => {
                      let value = Number(e.target.value);
                      if (isNaN(value)) value = 0;
                      if (value < 0) value = 0;
                      if (value > 100) value = 100;
                      setDiscountPercentage(value);
                    }}
                    disabled={loading}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              ) : (
                <InputGroup className="input-group">
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={discountRawAmount === 0 ? "" : discountRawAmount}
                    placeholder="0"
                    onChange={(e) => {
                      let value = Number(e.target.value);
                      if (isNaN(value)) value = 0;
                      if (value < 0) value = 0;
                      if (value > totalPayed) value = totalPayed;
                      setDiscountRawAmount(value);
                    }}
                    disabled={loading}
                  />
                </InputGroup>
              )}
            </div>

            <div className="preview-calculation">
              {discountType === "percentage" && (
                <>
                  <span className="preview-amount">
                    -{formatCurrency((discountPercentage / 100) * totalPayed)}
                  </span>
                  <span className="arrow-separator">→</span>
                </>
              )}
              <span className="final-amount">
                {discountType === "percentage"
                  ? formatCurrency(totalPayed - (discountPercentage / 100) * totalPayed)
                  : formatCurrency(totalPayed - discountRawAmount)}
              </span>
            </div>
          </div>

          {/* Row 3: Comment Field (Required) */}
          <div className="comment-row">
            <Form.Group className="comment-field">
              <Form.Label className="comment-label">
                Motivo del descuento <span className="required-indicator">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={comment}
                placeholder="Explica por qué se aplica este descuento post-pago..."
                onChange={(e) => setComment(e.target.value)}
                disabled={loading}
                maxLength={200}
                className="comment-textarea"
              />
              <div className="char-counter">
                {comment.length}/200 caracteres
              </div>
            </Form.Group>
          </div>

          {/* Row 4: Action Buttons */}
          <div className="discount-actions">
            <Button
              variant="none"
              className="action-button secondary"
              onClick={onClose}
              disabled={loading}
            >
              <FaX />
              Cancelar
            </Button>
            <Button
              variant="none"
              className="action-button primary"
              onClick={applyPostPaymentDiscount}
              disabled={
                loading ||
                !comment.trim() ||
                (discountType === "percentage" ? discountPercentage === 0 : discountRawAmount === 0)
              }
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  Aplicando...
                </>
              ) : (
                <>
                  <FaCheck />
                  Aplicar descuento
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Warning message */}
        <div className="discount-warning">
          <p>⚠️ Este descuento se aplicará a un recibo ya pagado. El sistema ajustará las transacciones reembolsables automáticamente.</p>
        </div>
      </div>
    </>
  );
};

export default PostPaymentDiscount;
